import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { GlassCard, SectionLabel } from "@/components/primitives";

export default function IncidentHeatmap() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/heatmap").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return null;
  const max = Math.max(1, ...data.grid.flat());

  return (
    <GlassCard className="p-6" data-testid="incident-heatmap">
      <div className="flex items-center justify-between">
        <div>
          <SectionLabel>Incident Heatmap</SectionLabel>
          <h3 className="text-lg font-semibold mt-1">Day of week × hour</h3>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
          <span>low</span>
          <div className="flex">
            {[0.1, 0.25, 0.45, 0.65, 0.85, 1].map((v, i) => (
              <span key={i} className="h-3 w-4" style={{ background: heatColor(v) }} />
            ))}
          </div>
          <span>high</span>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div className="inline-block">
          {/* hour header */}
          <div className="grid" style={{ gridTemplateColumns: `40px repeat(24, minmax(18px, 1fr))` }}>
            <span />
            {data.hours.map((h) => (
              <span key={h} className="text-[9px] font-mono text-slate-500 text-center">{h % 3 === 0 ? h : ""}</span>
            ))}
          </div>
          {data.grid.map((row, r) => (
            <div key={r} className="grid items-center mt-1" style={{ gridTemplateColumns: `40px repeat(24, minmax(18px, 1fr))` }}>
              <span className="text-[10px] font-mono text-slate-500">{data.days[r]}</span>
              {row.map((v, c) => {
                const intensity = v / max;
                return (
                  <motion.span
                    key={c}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: (r * 24 + c) * 0.002, duration: 0.25 }}
                    title={`${data.days[r]} ${c}:00 — ${v} incidents`}
                    className="h-4 mx-[1px] rounded-[3px] cursor-default"
                    style={{ background: heatColor(intensity) }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </GlassCard>
  );
}

function heatColor(v) {
  // 0 -> dim slate, 1 -> bright cyan→violet gradient
  const c = Math.max(0, Math.min(1, v));
  if (c < 0.04) return "rgba(255,255,255,0.04)";
  // Mix cyan (#00F0FF) to violet (#9D4CDD) based on intensity, alpha scales
  const r = Math.round(0 + (157 - 0) * c);
  const g = Math.round(240 + (76 - 240) * c);
  const b = Math.round(255 + (221 - 255) * c);
  const a = 0.12 + 0.85 * c;
  return `rgba(${r},${g},${b},${a})`;
}
